import type { StateCreator } from 'zustand'

// Measurement mode: which ThatOpen measurement tool is currently
// capturing canvas clicks. `null` means measurements are off and
// regular selection works normally. The exact strings encode both
// the tool kind and its sub-mode so the ribbon buttons can be
// modelled as a single radio group.
export type MeasureMode =
  | null
  | 'length-free'
  | 'length-edge'
  | 'area-free'
  | 'area-square'
  | 'area-face'

export type LengthUnit = 'mm' | 'cm' | 'm' | 'km'

// Work plane: a translucent reference plane drawn in the scene to
// give the user a 3D-space anchor while measuring. `kind` 'XY' is
// horizontal (normal +Y, the floor convention in three's Y-up world);
// 'XZ' is frontal (normal +Z); 'YZ' is lateral (normal +X); 'face'
// is set by picking a face on a model. `origin` is a world-space
// point on the plane; `normal` is the unit world-space normal.
export type WorkPlaneKind = 'XY' | 'XZ' | 'YZ' | 'face'
export interface WorkPlane {
  kind: WorkPlaneKind
  origin: [number, number, number]
  normal: [number, number, number]
}

export interface MeasureSlice {
  measureMode: MeasureMode
  // When true, every accumulated measurement (its label + line + fill)
  // is rendered. Toggle to "hide cotas" without losing them.
  measurementsVisible: boolean
  // Length unit applied to every length AND derived area unit
  // (m -> m2, cm -> cm2, etc). The slice keeps a single source of
  // truth; useMeasure mirrors to AreaMeasurement.units accordingly.
  measureUnits: LengthUnit
  measureRounding: number
  // Cursor world coordinates HUD (live read-out near the cursor).
  // Independent from measureMode: can be left on while picking, off
  // when not needed. cursorWorld is the cached position from the last
  // pointermove that hit a model surface or the ground plane.
  showCoordinates: boolean
  cursorWorld: [number, number, number] | null
  // Optional reference origin for relative coordinates. When set,
  // the HUD shows both absolute (world) and relative (delta to ref)
  // X/Y/Z. A fixed marker is also drawn in the scene at this point.
  coordsReference: [number, number, number] | null
  // Work plane (visual scaffolding for measurement). null = no plane.
  workPlane: WorkPlane | null
  // While true, the next click on geometry sets workPlane to the
  // picked face (origin = hit point, normal = face world normal).
  workPlanePickMode: boolean
  // When true (default), all annotation placements are projected onto
  // the active work plane. When false, the work plane stays drawn as
  // a visual reference but click-to-place uses the raw picked point.
  workPlaneSnap: boolean
  // When false, the work plane's visual representation (translucent
  // square + grid + border) is hidden, but the plane itself stays
  // active for placement. Useful to keep clicks projected onto a
  // reference plane without the orange overlay cluttering the view.
  workPlaneVisible: boolean
  // While true, the next canvas click captures the picked point as
  // the relative origin for coordinates (coordsReference). Cleared
  // automatically after one shot. Used by the HUD "definir origem
  // por click" button; before this we asked the user to first move
  // the cursor and then click a button, which lost the cursor target.
  coordsOriginPickMode: boolean
  // While true, clicking on a committed cota deletes it (instead of
  // selecting an element or starting a new measurement). Mutually
  // exclusive with measureMode: turning either on clears the other.
  measureDeleteMode: boolean
  // Snap class cycle for measurement / coord picking. 'all' = let
  // Fragments pick the best of point/line/face on its own; the
  // other values restrict the snap to a single class. Tab cycles
  // all → point → line → face → all, matching the convention used
  // by Revit / AutoCAD / FreeCAD.
  snapClass: 'all' | 'point' | 'line' | 'face'

  setMeasureMode: (mode: MeasureMode) => void
  setMeasurementsVisible: (on: boolean) => void
  setMeasureUnits: (units: LengthUnit) => void
  setShowCoordinates: (on: boolean) => void
  setCursorWorld: (p: [number, number, number] | null) => void
  setCoordsReference: (p: [number, number, number] | null) => void
  setWorkPlane: (plane: WorkPlane | null) => void
  setWorkPlanePickMode: (on: boolean) => void
  setWorkPlaneSnap: (on: boolean) => void
  setWorkPlaneVisible: (on: boolean) => void
  setMeasureDeleteMode: (on: boolean) => void
  setCoordsOriginPickMode: (on: boolean) => void
  cycleSnapClass: () => void
}

const UNITS_KEY = 'vn2r.measure.units'
const VISIBLE_KEY = 'vn2r.measure.visible'

// Older builds persisted the Coordenadas HUD on/off state under this
// key. Now the Coordenadas toggle in the ribbon also owns the
// click-to-place mode, so a stale "true" coming back across reloads
// looked like a phantom HUD. Wipe the legacy key on module load so
// users coming from an old build get a clean default.
try {
  localStorage.removeItem('vn2r.measure.coords')
} catch {
  /* ignore */
}

function loadUnits(): LengthUnit {
  try {
    const v = localStorage.getItem(UNITS_KEY)
    if (v === 'mm' || v === 'cm' || v === 'm' || v === 'km') return v
  } catch {
    /* ignore */
  }
  return 'm'
}

function loadBool(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key)
    if (v === null) return fallback
    return v !== 'false'
  } catch {
    return fallback
  }
}

function persist(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* ignore */
  }
}

export const createMeasureSlice: StateCreator<MeasureSlice> = (set) => ({
  measureMode: null,
  measurementsVisible: loadBool(VISIBLE_KEY, true),
  measureUnits: loadUnits(),
  measureRounding: 2,
  // Don't persist Coordenadas across sessions: the mode includes a
  // click-to-place behaviour now, and the user expects a clean slate
  // on every reload (a stale "on" state was confusing: the HUD came
  // back without an obvious cause). Manual toggle on each session.
  showCoordinates: false,
  cursorWorld: null,
  coordsReference: null,
  workPlane: null,
  workPlanePickMode: false,
  workPlaneSnap: true,
  workPlaneVisible: true,
  measureDeleteMode: false,
  coordsOriginPickMode: false,
  snapClass: 'all',

  setMeasureMode: (mode) =>
    set((s) => ({
      measureMode: mode,
      // Turning on a measure mode cancels delete mode and vice versa.
      measureDeleteMode: mode !== null ? false : s.measureDeleteMode,
    })),
  setMeasurementsVisible: (on) => {
    persist(VISIBLE_KEY, String(on))
    set({ measurementsVisible: on })
  },
  setMeasureUnits: (units) => {
    persist(UNITS_KEY, units)
    set({ measureUnits: units })
  },
  setShowCoordinates: (on) => {
    set({ showCoordinates: on, cursorWorld: null })
  },
  setCursorWorld: (p) => set({ cursorWorld: p }),
  setCoordsReference: (p) => set({ coordsReference: p }),
  setWorkPlane: (plane) =>
    set({ workPlane: plane, workPlanePickMode: false }),
  setWorkPlanePickMode: (on) => set({ workPlanePickMode: on }),
  setWorkPlaneSnap: (on) => set({ workPlaneSnap: on }),
  setWorkPlaneVisible: (on) => set({ workPlaneVisible: on }),
  setMeasureDeleteMode: (on) =>
    set((s) => ({
      measureDeleteMode: on,
      // Turning on delete cancels any active measure mode.
      measureMode: on ? null : s.measureMode,
    })),
  setCoordsOriginPickMode: (on) => set({ coordsOriginPickMode: on }),
  cycleSnapClass: () =>
    set((s) => {
      const order: MeasureSlice['snapClass'][] = [
        'all',
        'point',
        'line',
        'face',
      ]
      const i = order.indexOf(s.snapClass)
      return { snapClass: order[(i + 1) % order.length]! }
    }),
})
